import { Suspense } from "react";
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { UserIcon } from "lucide-react";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { DialogComponent } from "@/lib/Components/Dialog/Hooks/useDialog";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { BlogAuthorForm } from "@/app/Blogs/Forms/BlogAuthorForm";
import {
  blogAuthorRecordQuery,
  updateBlogAuthorMutation,
} from "@/app/Blogs/GraphQL/blogAuthorQueries";
import { createMediaMutation } from "@/app/Media/GraphQL/mediaMutations";
import { blogAuthorValidation } from "@/app/Blogs/Validation/blogValidation";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import {
  MediaCollection,
  ModelType,
  type UpdateBlogAuthorInput,
} from "@/gql/graphql";
import { FileUploader } from "@/lib/FileUpload/FileUploader";

type BlogAuthorEditDrawerProps = {
  authorId: string;
};

export const BlogAuthorEditDrawer: DialogComponent<
  BlogAuthorEditDrawerProps
> = ({ authorId }) => {
  const { isOpen, close } = useDialogState();

  return (
    <Drawer open={isOpen} onClose={close}>
      <DrawerOverlay />
      <DrawerContent showCloseButton onClose={close} className="max-w-2xl">
        <div className="flex h-full flex-col overflow-hidden">
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <Spinner />
              </div>
            }
          >
            <BlogAuthorEditDrawerContent authorId={authorId} />
          </Suspense>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

function BlogAuthorEditDrawerContent({ authorId }: { authorId: string }) {
  const { data } = useGqlQuery(blogAuthorRecordQuery, { id: authorId });
  const { mutateAsync: updateAuthor } = useGqlMutation(
    updateBlogAuthorMutation,
  );
  const { mutateAsync: createMedia } = useGqlMutation(createMediaMutation);

  const author = data?.blogAuthor;

  if (!author) {
    return (
      <DrawerBody>
        <p className="text-zinc-500">Author not found.</p>
      </DrawerBody>
    );
  }

  return (
    <>
      <DrawerHeader className="px-6 py-5">
        <div className="flex items-center gap-4">
          {author.image?.url ? (
            <img
              src={author.image.url}
              alt={author.name}
              className="h-14 w-14 rounded-full object-cover ring-1 ring-gray-200"
            />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 ring-1 ring-gray-200">
              <UserIcon className="h-6 w-6 text-gray-400" />
            </div>
          )}
          <div>
            <DrawerTitle>{author.name}</DrawerTitle>
            <DrawerDescription>{author.role ?? null}</DrawerDescription>
          </div>
        </div>
      </DrawerHeader>

      <DrawerBody className="space-y-6">
        <RecordScreenFormCard<UpdateBlogAuthorInput>
          title="Details"
          validationSchema={toFormikValidationSchema(blogAuthorValidation)}
          initialValues={{
            name: author.name,
            role: author.role,
            bio: author.bio,
          }}
          onSubmit={async (values) => {
            await updateAuthor({ id: author.id, input: values });
          }}
        >
          <BlogAuthorForm />
        </RecordScreenFormCard>

        <RecordScreenCard title="Profile picture">
          <div className="col-span-full space-y-4">
            {author.image?.url ? (
              <img
                src={author.image.url}
                alt={author.name}
                className="h-32 w-32 rounded-full object-cover ring-1 ring-gray-200"
              />
            ) : null}
            <FileUploader
              maxFileCount={1}
              onUpload={async (uploads) => {
                const uploaded = uploads[0];
                if (!uploaded) return;
                await createMedia({
                  input: {
                    file_name: uploaded.file.name,
                    objectId: author.id,
                    objectType: ModelType.BlogAuthor,
                    collectionName: MediaCollection.ProfilePicture,
                    key: uploaded.filePath,
                  },
                });
              }}
            />
          </div>
        </RecordScreenCard>
      </DrawerBody>
    </>
  );
}
